/**
 * Formats a number with Italian locale style (dot for thousands, comma for decimals)
 * Example: 1000.5 -> "1.000,50"
 */
export const formatItalianNumber = (value?: number | null, precision: number = 2): string => {
  if (value == null) return "-";
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  }).format(value);
};

/**
 * Formats a number with Italian locale style with custom default value
 * Example: 1000.5 -> "1.000,50", null -> "n.d."
 */
export const formatItalianNumberOrDefault = (value?: number | null, defaultValue?: string, precision: number = 2): string => {
  if (value == null) return defaultValue || "-";
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  }).format(value);
};

/**
 * Formats a number as percentage with Italian locale style (dot for thousands, comma for decimals)
 * Multiplies by 100 before formatting
 * Example: 0.054 -> "5,40%"
 */
export const formatItalianPercentage = (value?: number | null, precision: number = 2): string => {
  if (value == null) return "-";
  const formatted = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  }).format(value * 100);
  return `${formatted}%`;
};

/**
 * Formats a number as percentage with Italian locale style without multiplying by 100
 * Example: 3.82 -> "3,82%"
 */
export const formatItalianPercentageNo100 = (value?: number | null, precision: number = 2): string => {
  if (value == null) return "-";
  const formatted = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  }).format(value);
  return `${formatted}%`;
};

/**
 * Formats a number as currency with Italian locale style
 * Example: 1000.5 -> "1.000,50 €"
 */
export const formatItalianCurrency = (value?: number | null, precision: number = 2): string => {
  if (value == null) return "-";
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: true,
  }).format(value);
};